/**
 * Deterministic command pattern matcher for the Public Transcription page.
 *
 * Active command families (the voice-driven section state machine was
 * retired in favour of per-test Generate buttons; START/STOP scanning
 * is dormant — see the regex definitions below):
 *
 *  - NAVIGATE: "access <section>" / "open <section>" / "go to <section>".
 *              Scrolls the EHR to the matching test card and pulses its
 *              glow for ~3s. Does NOT touch the transcription buffer.
 *
 *  - DOCUMENT: fires the document workflow. The behaviour depends on
 *              the location's licensed AI Scribe sub-module (decided
 *              EHR-side, not here): in Global mode it triggers the
 *              encounter-level workflow; in Section mode it closes the
 *              most recently navigated section. Three bare phrases
 *              are accepted — all emit kind='document':
 *                (a) "access document"
 *                (b) "access done"
 *                (c) "access scribe"
 *
 *  - SPEAK:    "speak to me the external history" / "read internal
 *              history" → TTS history read-back via gateway → n8n LLM
 *              → Chirp HD Aura.
 *
 * Matching is case-insensitive, strips punctuation, and collapses whitespace.
 * Every keyword and every trigger is sent to Deepgram Nova-3 via the
 * `keyterm` parameter so recognition is biased toward these terms.
 *
 * Keep `KNOWN_COMMANDS` in sync with the EyePegasus sectionTag vocabulary
 * (see ehr-under-tabbar.component.ts → SECTION_TAG_TO_BUSINESS_KEY).
 * The `KEYWORD_TO_SECTION_TAG` mapping below translates the natural-language
 * keyword the user spoke into the canonical sectionTag the gateway expects.
 *
 * Wake word: bare "Access" — chosen so the patient never hears "AI" mentioned
 * during the visit. Phonetic fallbacks (excess, x s, xcess, assess, axes …)
 * absorb Deepgram's common mis-transcriptions of the same spoken word.
 */

export type CommandKind = 'start' | 'stop' | 'speak' | 'navigate' | 'document';

export interface DetectedCommand {
  /** start = begin capturing for keyword; stop = finalize the buffer. */
  kind: CommandKind;
  /** Natural-language keyword phrase, e.g. "external slit lamp" */
  keyword: string;
  /** Canonical sectionTag understood by the gateway / EyePegasus. */
  sectionTag: string;
  /** Raw matched substring incl. trigger, e.g. "access external slit lamp" */
  raw: string;
  /** Character offset within the normalized transcript */
  index: number;
}

/**
 * Known keyword phrases that complete the "(trigger) <X>" pattern. The
 * matcher sorts these by length so "external slit lamp" wins over "slit lamp"
 * and "history of present illness" wins over "hpi".
 */
// STRICT allow-list. This is the complete set of section keywords the
// voice scribe recognises — one block per supported section, mirroring
// the agreed command table exactly. Entries that look like duplicates
// (e.g. "h p i" alongside "hpi", "see see and h p i" alongside
// "cc and hpi") are NOT extra commands — they're the renderings
// Deepgram emits for the SAME spoken phrase (it spells two-letter
// abbreviations as separate tokens, hears "CC" as "see see", etc.).
// They all resolve to the same sectionTag, so the documented command
// actually fires regardless of how the recogniser tokenises it.
export const KNOWN_COMMANDS: readonly string[] = [
  // ---- 1. CC & HPI ----
  // Spoken: "Chief Complaint", "Chief Complaint and HPI", "CC and HPI", "HPI"
  'chief complaint and hpi',
  'chief complaint',
  'cc and hpi',
  'cc and h p i',
  'see see and hpi',
  'see see and h p i',
  'hpi',
  'h p i',
  // ---- 2. External / Slit Lamp ----
  // Spoken: "Slit Lamp", "External Slit Lamp", "Anterior", "Anterior Seg(ment)"
  'external slit lamp',
  'slit lamp',
  'anterior segment',
  'anterior seg',
  'anterior',
  // ---- 3. Internal / Ophthalmoscopy ----
  // Spoken: "Ophthalmoscopy", "Internal", "Posterior", "Posterior Seg(ment)"
  'ophthalmoscopy',
  'internal',
  'posterior segment',
  'posterior seg',
  'posterior',
  // ---- 4. Medical (combined Slit Lamp + Ophthalmoscopy) ----
  // Spoken: "Medical", "Medical Exam", "Medical Exams"
  'medical exams',
  'medical exam',
  'medical',
  // ---- 5. Refractions ----
  // Spoken: "Refraction", "Refractions"
  'refraction',
  'refractions',
  // ---- 6. Contact Lenses ----
  // Spoken: "Contact Lens", "Contact Lenses"
  'contact lens',
  'contact lenses',
  // ---- 7. Assessment & Plan ----
  // Spoken: "Assessment and Plan", "Assessment Plan", "DX and Plan",
  // "DX Plan" (incl. Deepgram letter-split "D X …")
  'assessment and plan',
  'assessment plan',
  'dx and plan',
  'dx plan',
  'd x and plan',
  'd x plan',
];

/**
 * Map each natural-language command keyword to the canonical sectionTag used
 * by the workflow-engine (`ScribeRequest.sectionTag`) and by EyePegasus
 * (`SECTION_TAG_TO_BUSINESS_KEY` in ehr-under-tabbar.component.ts).
 * Unmapped keywords fall back to the keyword itself.
 */
export const KEYWORD_TO_SECTION_TAG: Readonly<Record<string, string>> = {
  // ---- 1. CC & HPI → businessKeysMap.cchpi ('test.cchpi') ----
  'chief complaint and hpi': 'ccHpi',
  'chief complaint': 'ccHpi',
  'cc and hpi': 'ccHpi',
  'cc and h p i': 'ccHpi',
  'see see and hpi': 'ccHpi',
  'see see and h p i': 'ccHpi',
  hpi: 'ccHpi',
  'h p i': 'ccHpi',
  // ---- 2. External / Slit Lamp → 'external' (businessKeysMap.slitlamp) ----
  'external slit lamp': 'external',
  'slit lamp': 'external',
  'anterior segment': 'external',
  'anterior seg': 'external',
  anterior: 'external',
  // ---- 3. Internal / Ophthalmoscopy → 'internal' (businessKeysMap.ophthalmoscopy) ----
  ophthalmoscopy: 'internal',
  internal: 'internal',
  'posterior segment': 'internal',
  'posterior seg': 'internal',
  posterior: 'internal',
  // ---- 4. Medical → 'medicalExams' (combined Slit Lamp + Ophthalmoscopy,
  //         uploaded via /api/scribe/transcribe-medical-exams) ----
  medical: 'medicalExams',
  'medical exam': 'medicalExams',
  'medical exams': 'medicalExams',
  // ---- 5. Refractions → 'refractions' (businessKeysMap.refractions) ----
  refraction: 'refractions',
  refractions: 'refractions',
  // ---- 6. Contact Lenses → 'contact-lenses' (businessKeysMap.contactlenses) ----
  'contact lens': 'contact-lenses',
  'contact lenses': 'contact-lenses',
  // ---- 7. Assessment & Plan → 'assessmentAndPlan' (businessKeysMap.assessmentPlan) ----
  'assessment and plan': 'assessmentAndPlan',
  'assessment plan': 'assessmentAndPlan',
  'dx and plan': 'assessmentAndPlan',
  'dx plan': 'assessmentAndPlan',
  // Deepgram often spells "DX" as separate tokens "D X".
  'd x and plan': 'assessmentAndPlan',
  'd x plan': 'assessmentAndPlan',
};

/**
 * Trigger phrases for the NAVIGATE command (renamed from START — the
 * voice-driven section state machine was retired; section boundaries
 * are now driven by per-test Generate buttons in the EHR). When the
 * clinician says any of these followed by a known section keyword,
 * the EHR scrolls to that test card and pulses the spectrum-ring glow
 * for a few seconds. The transcription buffer is NOT touched — the
 * user dictates freely and clicks Generate on whatever card when
 * they're ready.
 *
 * Three families:
 *
 *   1. "Access" wake-word + its Deepgram-misheard phonetic neighbors
 *      (excess / assess / asses / axes / axis / ask us / smart-format
 *      spell-outs "x s", "xcess", "x ess", "ex s", "xs").
 *
 *   2. Navigation verb phrases — these read naturally and let the
 *      clinician avoid the "Access" wake word entirely:
 *      "navigate to / navigate me to / go to / open / scroll to /
 *      scroll down to / scroll up to / show me / take me to".
 *
 *   3. (No STOP-family triggers — STOP voice commands were removed
 *      when section state migrated to Generate-button clicks.)
 *
 * False-positive note: "open" and "go to" are common clinical words,
 * but the START_PATTERN regex requires a known section keyword
 * immediately after, so e.g. "open the eye" won't fire (no section
 * keyword 'eye') while "open the cornea" would — cornea is in the
 * matcher's keyword list. Acceptable trade-off for the more natural
 * phrasing.
 */
export const START_TRIGGER_VARIANTS: readonly string[] = [
  // ---- Family 1: "Access" wake-word + phonetic neighbours ----
  'access',
  'excess',
  'assess',
  'asses',
  'axes',
  'axis',
  'ask us',
  // smart_format spell-outs of "access"
  'x s',
  'xcess',
  'x ess',
  'ex s',
  'xs',
  // ---- Family 2: natural-language navigation verbs ----
  'navigate to',
  'navigate me to',
  'go to',
  'open',
  'scroll to',
  'scroll down to',
  'scroll up to',
  'show me',
  'take me to'
];

/**
 * STOP-trigger vocabulary — STRICT. The only supported stop command is
 * "Access Done" (per the agreed command table). There is exactly one
 * tier now:
 *
 *   STRONG — "access done" branded form (+ the phonetic renderings of
 *   "access" that Deepgram emits). Accepted anywhere in a segment. An
 *   optional trailing section keyword is allowed ("access done
 *   refraction") but never required — "access done" alone closes
 *   whichever section is active.
 *
 * The old WEAK tier (bare "stop"/"done"/"finish" at segment start) and
 * the "<section> <verb>" SUFFIX form were removed: they fired on normal
 * clinical narrative and aren't part of the supported command set.
 * STOP_TRIGGERS_WEAK is kept as an empty array so the matcher can cheaply
 * detect "no weak triggers" and skip the weak/suffix scans.
 */
// Phonetic renderings of "access" Deepgram emits — shared by NAVIGATE
// ("access X"), STOP ("access done"), and DOCUMENT ("access document").
const ACCESS_VARIANTS = [
  'access',
  'excess',
  'assess',
  'asses',
  'axes',
  'axis',
  'ask us',
  'x s',
  'xcess',
  'x ess',
  'ex s',
  'xs',
] as const;

const STOP_VERBS = ['done'] as const;

const STOP_TRIGGERS_STRONG: readonly string[] =
  ACCESS_VARIANTS.flatMap((a) => STOP_VERBS.map((v) => `${a} ${v}`));

const STOP_TRIGGERS_WEAK: readonly string[] = [];

export const STOP_TRIGGER_VARIANTS: readonly string[] = [
  ...STOP_TRIGGERS_STRONG,
  ...STOP_TRIGGERS_WEAK,
];

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeForRegex(phrase: string): string {
  return normalize(phrase).replace(/\s+/g, '\\s+');
}

function alts(items: readonly string[]): string {
  const sorted = [...items].sort((a, b) => b.length - a.length);
  return sorted.map(escapeForRegex).join('|');
}

const CMD_ALTS = alts(KNOWN_COMMANDS);
const START_TRIG_ALTS = alts(START_TRIGGER_VARIANTS);
const STOP_STRONG_TRIG_ALTS = alts(STOP_TRIGGERS_STRONG);
const STOP_WEAK_TRIG_ALTS = alts(STOP_TRIGGERS_WEAK);

// Optional filler words clinicians naturally drop between the command verb
// and the section keyword: "stop *the* ophthalmoscopy", "complete *this*
// slit lamp", "I'm done with *the* HPI". Matched non-capturing so the
// section keyword stays in capture group 1.
const PRE_SECTION_FILLER = '(?:(?:the|this|that|with|with\\s+the|on|on\\s+the|to|to\\s+the)\\s+)?';
const POST_SECTION_FILLER = '(?:\\s+(?:is|are|now|here))?';

// "(start trigger) [filler] (section)"  →  group 1 = section keyword
const START_PATTERN = new RegExp(
  `\\b(?:${START_TRIG_ALTS})\\s+${PRE_SECTION_FILLER}(${CMD_ALTS})\\b`,
  'gi'
);

// STRONG STOP: branded form. The section keyword after the branded trigger
// is OPTIONAL — "access stop" on its own is a valid close. When a section
// keyword is captured, the matcher routes the close to that specific
// section; otherwise it closes whichever section is currently active.
const STOP_STRONG_PATTERN = new RegExp(
  `\\b(?:${STOP_STRONG_TRIG_ALTS})(?:\\s+${PRE_SECTION_FILLER}(${CMD_ALTS}))?\\b`,
  'gi'
);

// WEAK STOP: bare verbs ("stop ophthalmoscopy", "finish refraction"). Only
// matches at the START of a segment or right after sentence-final
// punctuation — this is what protects clinical narrative like "done with
// cornea transplant" from accidentally closing the section.
const STOP_WEAK_PATTERN = new RegExp(
  `(?:^|[.?!]\\s+)(?:${STOP_WEAK_TRIG_ALTS})\\s+${PRE_SECTION_FILLER}(${CMD_ALTS})\\b`,
  'gi'
);

// SUFFIX STOP: "<section> <verb>" e.g. "ophthalmoscopy done". Anchored to
// the END of a segment (or before sentence-final punctuation) so a phrase
// like "ophthalmoscopy done in the previous visit" doesn't trigger.
const STOP_SUFFIX_PATTERN = new RegExp(
  `\\b(${CMD_ALTS})${POST_SECTION_FILLER}\\s+(?:${STOP_WEAK_TRIG_ALTS})\\b(?=\\s*(?:$|[.?!]))`,
  'gi'
);

// ---------------- DOCUMENT pattern (Generate trigger) --------------------
//
// Three bare trigger phrases — these are the ONLY commands that fire the
// document workflow. The behaviour they invoke is decided downstream by
// the EHR, based on which AI Scribe module the location has licensed:
//
//   - "access document"
//   - "access done"
//   - "access scribe"
//
// All three emit kind='document' with NO section keyword (the matcher's
// `sectionTag` field stays empty). The under-tabbar handler then routes:
//   - aiScribeGlobal licensed  → run the encounter-level global workflow
//   - aiScribeSection licensed → close the most-recently navigated
//                                section (snackbar if nothing navigated)
//
// Earlier "<access> document <section>" and bare "document <section>"
// variants were removed — the trigger set is intentionally minimal so
// the same vocabulary works identically across both modes.
const ACCESS_VARIANT_ALTS = alts(ACCESS_VARIANTS);

const DOCUMENT_VERBS = ['document', 'done', 'scribe'] as const;
const DOCUMENT_VERB_ALTS = alts(DOCUMENT_VERBS);

const DOCUMENT_TRIGGER_PATTERN = new RegExp(
  // "<access> <document|done|scribe>" — strict bare form. No optional
  // trailing section keyword; matcher emits kind='document' with empty
  // sectionTag and the EHR resolves the target by module mode.
  `\\b(?:${ACCESS_VARIANT_ALTS})\\s+(?:${DOCUMENT_VERB_ALTS})\\b`,
  'gi'
);

// ---------------- Voice-command strip helper ------------------------------
//
// The section's transcription is what reaches n8n's LLM, which extracts
// findings. Embedded command tokens ("access chief complaint", "access
// document", "document refraction") confuse the LLM — it can either
// treat the command as content or skip nearby content while parsing
// the command. Removing every recognised command phrase before the
// slice ships keeps the LLM input clean.
//
// Operates on RAW (unnormalised) text — same case-insensitive matching
// the matchers use but tolerant of punctuation between tokens (Deepgram
// emits "access chief complaint." with a trailing period; the normalised
// matchers don't see the period but a raw-text regex must).
const RAW_WS = '[\\s.,;:!?]+';
function rawAlts(items: readonly string[]): string {
  const sorted = [...items].sort((a, b) => b.length - a.length);
  return sorted
    .map((s) => normalize(s).replace(/\s+/g, RAW_WS))
    .join('|');
}
const RAW_ACCESS_ALTS = rawAlts(ACCESS_VARIANTS);
const RAW_START_TRIG_ALTS = rawAlts(START_TRIGGER_VARIANTS);
const RAW_CMD_ALTS = rawAlts(KNOWN_COMMANDS);
const RAW_DOCUMENT_VERB_ALTS = rawAlts(DOCUMENT_VERBS);

const STRIP_PATTERN_RAW = new RegExp(
  `\\b(?:` +
    // Navigate: "<access|open|go to|...> <section>"
    `(?:${RAW_START_TRIG_ALTS})${RAW_WS}(?:${RAW_CMD_ALTS})` +
    `|` +
    // Document trigger: "<access> <document|done|scribe>" — bare only.
    `(?:${RAW_ACCESS_ALTS})${RAW_WS}(?:${RAW_DOCUMENT_VERB_ALTS})` +
  `)\\b`,
  'gi'
);

/**
 * Remove every recognised voice-command phrase from a raw transcript
 * chunk so the n8n LLM doesn't ingest command tokens as content. Used
 * by PublicTranscriptionService on every section slice (defence in
 * depth — the service also drops command-trigger segments at receive
 * time, but a stray phrase inside a mixed segment can still slip
 * through). Punctuation and casing in the surrounding text are
 * preserved; only matched command spans are removed.
 */
export function stripVoiceCommands(text: string): string {
  if (!text) return text;
  return text
    .replace(STRIP_PATTERN_RAW, '')
    // Collapse any stray double-spaces / leading punctuation left by the
    // removal so the cleaned text still reads naturally.
    .replace(/\s+([.,;:!?])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

// ---------------- SPEAK pattern (history read-back) ----------------------
//
// Listens for phrases like:
//   "speak to me the external history of the last encounter"
//   "read internal history"
//   "tell me the ophthalmoscopy"
//   "recap slit lamp"
//   "playback fundus history"
//
// Emits a `speak` command with sectionTag set to either 'externalHistory'
// or 'internalHistory'. The parent EHR handles extraction of the actual
// findings text from cached patient history.
const SPEAK_TRIGGERS = [
  'speak to me',
  'speak',
  'read me',
  'read',
  'tell me',
  'say',
  'recite',
  'narrate',
  'recap',
  'playback',
  'play back'
] as const;
const SPEAK_TRIG_ALTS = alts(SPEAK_TRIGGERS);

// Synonyms grouped by target sectionTag. Sorting longest-first within each
// group so "external slit lamp history" beats "external" when both could
// match the same span.
const SPEAK_EXTERNAL_KEYWORDS = [
  'external slit lamp history',
  'external slit lamp',
  'external history',
  'slit lamp history',
  'slit lamp exam',
  'slit lamp',
  'cornea history',
  'cornea',
  'external'
] as const;
const SPEAK_INTERNAL_KEYWORDS = [
  'internal ophthalmoscopy history',
  'internal ophthalmoscopy',
  'internal history',
  'ophthalmoscopy history',
  'ophthalmoscopy',
  'fundus history',
  'fundus',
  'retina history',
  'retina',
  'internal'
] as const;
const SPEAK_EXTERNAL_ALTS = alts(SPEAK_EXTERNAL_KEYWORDS);
const SPEAK_INTERNAL_ALTS = alts(SPEAK_INTERNAL_KEYWORDS);

// Optional trailing phrase the clinician naturally tacks on but the
// matcher should accept: "...of the last encounter", "...of last visit",
// "...history", etc. Non-capturing.
const SPEAK_TAIL =
  '(?:\\s+history)?' +
  '(?:\\s+(?:of|from)(?:\\s+(?:the|my|patient))?(?:\\s+last|\\s+previous|\\s+prior)?' +
  '(?:\\s+(?:encounter|visit|exam|appointment))?)?';
const SPEAK_PRE_FILLER = '(?:(?:to\\s+me\\s+)?(?:the\\s+|last\\s+|previous\\s+|prior\\s+|my\\s+|patient\\s+)*)?';

const SPEAK_EXTERNAL_PATTERN = new RegExp(
  `\\b(?:${SPEAK_TRIG_ALTS})\\s+${SPEAK_PRE_FILLER}(${SPEAK_EXTERNAL_ALTS})${SPEAK_TAIL}\\b`,
  'gi'
);
const SPEAK_INTERNAL_PATTERN = new RegExp(
  `\\b(?:${SPEAK_TRIG_ALTS})\\s+${SPEAK_PRE_FILLER}(${SPEAK_INTERNAL_ALTS})${SPEAK_TAIL}\\b`,
  'gi'
);

// Toggle by setting `localStorage.VOICE_DEBUG = '1'` in the browser to log
// every matcher invocation with the input transcript and detected commands.
function isVoiceDebug(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      window.localStorage &&
      window.localStorage.getItem('VOICE_DEBUG') === '1'
    );
  } catch {
    return false;
  }
}

function toSectionTag(keyword: string): string {
  return KEYWORD_TO_SECTION_TAG[keyword] || keyword.replace(/\s+/g, '-');
}

function scan(
  pattern: RegExp,
  kind: CommandKind,
  normalized: string,
  out: DetectedCommand[]
): void {
  pattern.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(normalized)) !== null) {
    // The strong STOP pattern has an OPTIONAL section capture — e.g.
    // "access stop" on its own (no section keyword) is a valid close.
    // In that case the keyword/sectionTag are empty and the service
    // closes whichever section is currently active.
    const raw = m[1] ? m[1].replace(/\s+/g, ' ').trim() : '';
    out.push({
      kind,
      keyword: raw,
      sectionTag: raw ? toSectionTag(raw) : '',
      raw: m[0],
      index: m.index,
    });
  }
}

/**
 * SPEAK-specific scanner — same shape as scan() but tags every match
 * with a fixed sectionTag ('externalHistory' or 'internalHistory') so
 * the consumer can route the read-back without needing to interpret
 * the captured keyword text.
 */
function scanSpeak(
  pattern: RegExp,
  sectionTag: 'externalHistory' | 'internalHistory',
  normalized: string,
  out: DetectedCommand[]
): void {
  pattern.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(normalized)) !== null) {
    const raw = m[1] ? m[1].replace(/\s+/g, ' ').trim() : '';
    out.push({
      kind: 'speak',
      keyword: raw,
      sectionTag,
      raw: m[0],
      index: m.index,
    });
  }
}

/**
 * Scan a final transcript chunk for NAVIGATE and SPEAK commands. Call
 * only on Deepgram results where `is_final === true`. The returned list
 * is sorted by position in the transcript so consumers can process them
 * in dictation order.
 *
 *   - NAVIGATE  — "Access &lt;section&gt;" / "Open &lt;section&gt;" / "Go to …"
 *     etc. The EHR scrolls to the matching test card and pulses its
 *     glow for ~3s. Does NOT touch the transcription buffer — section
 *     boundaries are owned by the per-test Generate buttons.
 *
 *   - SPEAK     — "Speak to me the external history" etc. Triggers
 *     Deepgram Aura TTS history read-back. Independent of section
 *     recording.
 *
 * STOP scanning was retired with the voice-driven section state
 * machine. The STOP_*_PATTERN definitions are kept above (dormant) in
 * case we ever need them again.
 */
export function detectCommands(transcript: string): DetectedCommand[] {
  if (!transcript) return [];
  const normalized = normalize(transcript);
  const all: DetectedCommand[] = [];
  // DOCUMENT first — scanning the bare "<access> <document|done|scribe>"
  // pattern before NAVIGATE ensures the longest-wins dedup keeps the
  // document command in the rare case Deepgram emits something like
  // "access document refraction" as one final (the navigate pattern
  // would otherwise try to match "access refraction" out of the tail).
  scan(DOCUMENT_TRIGGER_PATTERN, 'document', normalized, all);
  // NAVIGATE: re-use the existing START_PATTERN (its shape is exactly
  // "<trigger phrase> <section keyword>" — same regex, just relabelled
  // at emit-time below as kind='navigate' instead of 'start').
  scan(START_PATTERN, 'navigate', normalized, all);
  // SPEAK (history read-back): two patterns, one per target sectionTag.
  // Scanned LAST so the overlap-dedup step below (which keeps earlier
  // matches over later overlapping ones) doesn't suppress a START/STOP
  // that happens to share a span with a SPEAK match.
  scanSpeak(SPEAK_EXTERNAL_PATTERN, 'externalHistory', normalized, all);
  scanSpeak(SPEAK_INTERNAL_PATTERN, 'internalHistory', normalized, all);

  // Sort by (a) starting index ascending, then (b) raw-length descending
  // so longer matches at the same offset win — e.g. when both
  // "cc end hpi" (START) and "end hpi" (STOP) match the same span, the
  // START is the user's true intent and the inner STOP is a false-positive
  // we must discard.
  all.sort((a, b) => {
    if (a.index !== b.index) return a.index - b.index;
    return b.raw.length - a.raw.length;
  });

  const seen = new Set<string>();
  const unique: DetectedCommand[] = [];
  let lastEnd = -1; // exclusive: index of the first char NOT covered by the previous kept match
  for (const c of all) {
    const key = `${c.kind}|${c.keyword}|${c.index}`;
    if (seen.has(key)) continue;
    seen.add(key);
    // Skip any match whose start falls inside the span of a previously
    // accepted match. Stops a START like "cc end hpi" from being followed
    // by a phantom STOP for the embedded "end hpi" — the user's real
    // intent was the longer phrase.
    if (c.index < lastEnd) continue;
    unique.push(c);
    lastEnd = c.index + c.raw.length;
  }

  if (isVoiceDebug()) {
    // eslint-disable-next-line no-console
    console.debug(
      '[VOICE_DEBUG] detectCommands',
      JSON.stringify({ transcript, normalized, matches: unique })
    );
  }

  return unique;
}

/**
 * Deepgram `keyterm` list — biases Nova-3's posterior toward these phrases.
 *
 * Biasing dilutes with the number of terms, so we focus on phrases that
 * actually move the needle: the multi-word "access <verb>" forms, the
 * combined-exam command, and section keywords with regional pronunciation
 * variation. Bare common English words ("access", "stop", "end") are
 * deliberately omitted — Nova-3 already recognises them perfectly and
 * spending budget on them would crowd out the rarer phrases.
 */
export function getKeyterms(): string[] {
  return [
    // Navigation triggers + section phrases. Biasing helps Deepgram
    // keep the "<trigger> <section>" pair in a single final segment.
    'access medical',
    'access medical exam',
    'access ophthalmoscopy',
    'access slit lamp',
    'access external slit lamp',
    'access anterior segment',
    'access posterior segment',
    'access internal',
    'access chief complaint',
    'access refraction',
    'access contact lens',
    'access assessment and plan',
    // Natural-language navigation verbs (broader than "access").
    'navigate to',
    'navigate me to',
    'go to',
    'scroll to',
    'scroll down to',
    'scroll up to',
    'show me',
    'take me to',
    // DOCUMENT (Generate-trigger) phrases. The three bare forms are
    // the ONLY commands that fire the document workflow — behaviour
    // depends on the location's licensed AI Scribe sub-module (global
    // vs section). Biasing keeps Deepgram from splitting the two-word
    // phrase across separate finals.
    'access document',
    'access done',
    'access scribe',
    // SPEAK / history read-back combos. The verb + section phrasing is
    // distinctive enough that biasing here genuinely helps.
    'speak to me',
    'read external history',
    'read internal history',
    'tell me external history',
    'tell me internal history',
    'recap external',
    'recap internal',
    'playback external history',
    'playback internal history',
    // Section keywords (medical vocabulary; Nova-3 benefits from biasing
    // on these because clinicians pronounce them with regional variation).
    ...KNOWN_COMMANDS,
  ];
}
